import { Component, ChangeDetectorRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { Header } from '../../components/header/header';
import { Footer } from '../../components/footer/footer';
import { MetaService } from '../../core/services/meta.service';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, Header, Footer],
  templateUrl: './change-password.html',
  styleUrls: ['./change-password.css']
})
export class ChangePasswordComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private metaService = inject(MetaService);

  form!: FormGroup;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  passwordStrength: 'weak' | 'medium' | 'strong' = 'weak';
  passwordUpdated = false;

  ngOnInit(): void {
    this.metaService.resetToDefault();

    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }

    this.form = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.minLength(8), this.mediumPasswordValidator]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    this.form.get('newPassword')?.valueChanges.subscribe(password => {
      this.updatePasswordStrength(password);
    });
  }

  get passwordMismatch(): boolean {
    return !!this.form.errors?.['passwordMismatch'] && !!this.form.get('confirmPassword')?.touched;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.authService.changePassword({
      currentPassword: this.form.value.currentPassword,
      newPassword: this.form.value.newPassword
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = response.data?.message || response.message || 'Contrasena cambiada exitosamente.';
          this.passwordUpdated = true;
          this.form.reset();
        } else {
          this.errorMessage = response.message || 'No se pudo cambiar la contrasena.';
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.errorMessage = error.error?.message || 'No se pudo cambiar la contrasena.';
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private mediumPasswordValidator = (control: AbstractControl): ValidationErrors | null => {
    const password = String(control.value || '');
    if (!password) return null;

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const minLength = password.length >= 8;

    return hasUpperCase && hasLowerCase && hasNumber && minLength ? null : { mediumPassword: true };
  };

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  private updatePasswordStrength(password: string): void {
    const safePassword = String(password || '');
    if (!safePassword) {
      this.passwordStrength = 'weak';
      return;
    }

    const hasUpperCase = /[A-Z]/.test(safePassword);
    const hasLowerCase = /[a-z]/.test(safePassword);
    const hasNumber = /\d/.test(safePassword);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(safePassword);
    const minLength = safePassword.length >= 8;

    const checks = [hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar, minLength].filter(Boolean).length;

    if (checks <= 2) {
      this.passwordStrength = 'weak';
    } else if (checks <= 4) {
      this.passwordStrength = 'medium';
    } else {
      this.passwordStrength = 'strong';
    }
  }
}
